import { checkReportRange, type ChecklistComplianceRow, type ReportResult } from "@fts/core";
import { PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { checklistSheet } from "../../lib/report-sheets";
import type { Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { ExportBar, FilterBar, Loading, ReportMeta, filterSummary, parseFilters, rangeKey, useLoad, type ExportSpec } from "./shared";

/** Kontrol listesi uyum raporu (ADR 0018): shifts used, checklists submitted, missed, compliance, faults, MTTR. */
export function ChecklistReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const issue = checkReportRange(f.from, f.to);
  const tenantId = tenant?.id;
  const query = (id: string) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, operatorId: null, from: f.from, to: f.to });
  const state = useLoad<ReportResult<ChecklistComplianceRow>>(
    tenantId && !issue ? () => provider.reportChecklist(query(tenantId)) : null,
    [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.from, f.to],
  );
  const rows = state.data?.rows ?? [];
  const used = rows.reduce((s, r) => s + (r.shiftsUsed ?? 0), 0);
  const checked = rows.reduce((s, r) => s + (r.shiftsChecked ?? 0), 0);
  const num = (v: number) => v.toLocaleString(locale, { maximumFractionDigits: 1 });
  const spec: ExportSpec | null = state.status === "ready" && state.data && tenantId
    ? {
        report: "checklist",
        reportName: t("reports.checklist.title"),
        slug: t("reports.checklist.slug"),
        part: `${f.from}_${f.to}`,
        tz: state.data.tz,
        filters: filterSummary(t, data, f, { operator: false }),
        rows: rows.length,
        load: async (format) => {
          const r = await provider.exportChecklist(query(tenantId), format);
          return { sheets: [checklistSheet(t, r.rows)], tz: r.tz };
        },
      }
    : null;
  const rk = rangeKey(issue);

  return (
    <>
      <PageHeader title={t("reports.checklist.title")} subtitle={t("reports.checklist.subtitle")} eyebrow={t("nav.group.reports")} />
      <FilterBar data={data} path="/reports/checklist" filters={f} operator={false} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      <ReportMeta tz={state.data?.tz ?? null} sample={provider.mode === "demo"} />
      {state.status === "ready" && used > 0 && (
        <div className="report-totals">
          <div>
            <span>{t("reports.checklist.totalCompliance")}</span>
            <strong>%{num((100 * checked) / used)}</strong>
          </div>
          <div>
            <span>{t("reports.col.shiftsUsed")}</span>
            <strong>{used}</strong>
          </div>
          <div>
            <span>{t("reports.col.missed")}</span>
            <strong>{used - checked}</strong>
          </div>
        </div>
      )}
      <ExportBar spec={spec} disabled={rows.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      <Panel flush meta={state.status === "ready" ? <span>{t("reports.rows", { count: rows.length })}</span> : undefined}>
        {state.status !== "ready" && state.status !== "error" ? (
          rk ? <p className="table-empty">{t(rk)}</p> : <Loading />
        ) : rows.length === 0 ? (
          <p className="table-empty">{t("reports.noData")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table report-table">
              <thead>
                <tr>
                  <th scope="col">{t("reports.col.vehicle")}</th>
                  <th scope="col">{t("reports.col.shift")}</th>
                  <th scope="col" className="num">{t("reports.col.shiftsUsed")}</th>
                  <th scope="col" className="num">{t("reports.col.shiftsChecked")}</th>
                  <th scope="col" className="num">{t("reports.col.missed")}</th>
                  <th scope="col" className="num">{t("reports.col.compliance")}</th>
                  <th scope="col" className="num col-hide-sm">{t("reports.col.faultsOpened")}</th>
                  <th scope="col" className="num col-hide-sm">{t("reports.col.faultsResolved")}</th>
                  <th scope="col" className="num col-hide-md">{t("reports.col.mttr")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.vehicleId}|${r.shiftName ?? ""}`}>
                    <td className="mono">{r.fleetNo}</td>
                    <td>{r.hasShifts ? (r.shiftName ?? t("common.empty")) : <span className="muted">{t("reports.checklist.noShifts")}</span>}</td>
                    <td className="num">{r.shiftsUsed ?? t("common.empty")}</td>
                    <td className="num">{r.shiftsChecked ?? t("common.empty")}</td>
                    <td className="num">{r.missed ? <strong className="text-crit">{r.missed}</strong> : (r.missed ?? t("common.empty"))}</td>
                    <td className="num">{r.compliance === null ? t("common.empty") : `%${num(r.compliance)}`}</td>
                    <td className="num col-hide-sm">{r.faultsOpened}</td>
                    <td className="num col-hide-sm">{r.faultsResolved}</td>
                    <td className="num col-hide-md">{r.mttrH === null ? t("common.empty") : t("reports.hours", { h: num(r.mttrH) })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">{t("reports.checklist.rules")}</p>
    </>
  );
}
